<script setup>
const route = useRoute();
const siteUrl = 'https://gefestai.com';
const slug = route.params.category_slug;

// Загружаем категорию вместе с вложенными подкатегориями
const { data: category, pending, error } = await useFetch(`${siteUrl}/api/category/${slug}`, {
    key: `category-page-${slug}`,
    transform: (res) => res.data || res
});
useSeoMeta({
    title: category.value?.meta_title || category.value?.name,
    description: category.value?.meta_description || category.value?.description,
    ogTitle: category.value?.meta_title || category.value?.name,
    ogDescription: category.value?.meta_description || category.value?.description,
});

useHead({
    link: [
        { rel: 'canonical', href: `${siteUrl}${route.path}` }
    ]
});
// ID выбранной подкатегории (null = показать всё из всех подкатегорий)
const activeSubId = ref(null);

// Вычисляем статьи для вывода
const filteredArticles = computed(() => {
    if (!category.value || !category.value.subcategories) return [];

    if (activeSubId.value) {
        // Статьи только из выбранной подкатегории
        const sub = category.value.subcategories.find(s => s.id === activeSubId.value);
        return sub?.articles || [];
    }

    // "Все": Собираем статьи из всех подкатегорий в один массив
    const all = category.value.subcategories.flatMap(sub => sub.articles || []);
    // Убираем дубликаты, если одна статья привязана к разным субкатегориям
    return [...new Map(all.map(item => [item.id, item])).values()];
});

</script>

<template>
    <div class="min-h-screen bg-[#0F1113] text-gray-300 relative flex flex-col font-sans">

        <div v-if="pending" class="flex-grow flex items-center justify-center">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
        </div>

        <template v-else-if="category">
            <nav class="relative z-20 px-6 py-6 max-w-7xl mx-auto w-full">
                <div class="flex items-center gap-3 text-sm font-medium">
                    <NuxtLink to="/blog" class="text-gray-500 hover:text-white transition-colors">База знаний</NuxtLink>
                    <span class="text-gray-700">/</span>
                    <span class="text-orange-400">{{ category.name }}</span>
                </div>
            </nav>

            <header class="relative z-10 px-6 pt-8 pb-16 max-w-7xl mx-auto w-full animate-fade-in-up">
                <div class="max-w-3xl">
                    <div class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 text-xs font-bold uppercase tracking-widest mb-6">
                        Раздел
                    </div>
                    <h1 class="text-5xl md:text-7xl font-extrabold text-white tracking-tighter mb-6 uppercase">
                        {{ category.name }}
                    </h1>
                    <p class="text-xl text-gray-400 leading-relaxed">
                        {{ category.description }}
                    </p>
                </div>
            </header>

            <main class="relative z-10 flex-grow px-4 sm:px-6 lg:px-8 pb-24">
                <div class="max-w-7xl mx-auto">
                    <div v-if="category.subcategories && category.subcategories.length"
                         class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

                        <NuxtLink
                            v-for="(sub, index) in category.subcategories"
                            :key="sub.id"
                            :to="`/blog/${category.slug}/${sub.slug}`"
                            class="group relative flex flex-col rounded-[2.5rem] overflow-hidden bg-[#131416] border border-white/5 hover:border-orange-500/30 transition-all duration-500 h-[400px]"
                        >
                            <div class="relative w-full h-48 overflow-hidden">
                                <img
                                    v-if="sub.articles && sub.articles.length > 0"
                                    :src="`${siteUrl}/storage/${sub.articles[0].cover_image}`"
                                    class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 opacity-50 group-hover:opacity-80"
                                />
                                <div v-else class="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                                    <svg class="w-12 h-12 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                </div>

                                <div class="absolute inset-0 bg-gradient-to-t from-[#131416] via-[#131416]/20 to-transparent"></div>

                                <div class="absolute top-4 right-4 px-3 py-1 rounded-full bg-black/40 backdrop-blur-md border border-white/10 text-[10px] font-bold text-white uppercase tracking-widest">
                                    {{ sub.articles_count || 0 }} материалов
                                </div>
                            </div>

                            <div class="relative z-10 p-8 pt-2 flex flex-col flex-grow">
                                <h3 class="text-2xl font-bold text-white group-hover:text-orange-400 transition-colors mb-3">
                                    {{ sub.name }}
                                </h3>

                                <p class="text-gray-500 text-sm leading-relaxed line-clamp-3 mb-6">
                                    {{ sub.description }}
                                </p>

                                <div class="mt-auto flex items-center text-xs font-bold text-orange-500 uppercase tracking-[0.2em] transition-all transform group-hover:translate-x-2">
                                    <span>Перейти в раздел</span>
                                    <svg class="w-4 h-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                    </svg>
                                </div>
                            </div>
                        </NuxtLink>
                    </div>

                    <div v-else class="text-center py-20 bg-white/5 rounded-[3rem] border border-dashed border-white/10">
                        <p class="text-gray-500 uppercase tracking-widest text-sm">В этой категории пока нет подразделов</p>
                    </div>
                </div>
            </main>
        </template>
    </div>
</template>

<style scoped>
.animate-pulse-slow {
  animation: pulse 12s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

.animate-fade-in-up {
  animation: fadeInUp 0.6s ease-out forwards;
  opacity: 0;
}

/* Скрытие скроллбара для горизонтального меню фильтров */
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
